/**
 * An assignment's description, read as a checklist.
 *
 * Mentors already write assignments as a list of tasks, one per line, but the
 * description was rendered as a single paragraph so the lines ran together.
 * Every non-empty line is a task here, and the student ticks them off.
 *
 * There is no separate tasks table: the description stays the single place the
 * list is authored, so every assignment that already exists becomes a
 * checklist with no migration and mentors carry on typing the way they do.
 */

// Bullets people actually type, stripped so "- Build the rig", "• Build the
// rig" and "1. Build the rig" are one task rather than three spellings of it.
// Also "[ ]" / "[x]", which someone writing a checklist by hand will reach for.
const BULLET = /^\s*(?:[-*•–]|\d+[.)]|\[\s*[xX ]?\s*\])\s*/;

const stripBullet = (line) => line.replace(BULLET, '').trim();

/**
 * Stable key for a task, derived from its own text.
 *
 * Deliberately NOT the line index: inserting a line at the top would shift
 * every index below it and slide the student's ticks onto the wrong tasks.
 * Hashing the text means other lines can be edited, reordered or deleted
 * freely. Editing *this* task's text does lose its tick — correct, it is a
 * different task now.
 *
 * FNV-1a, 32-bit, hex. Not a security boundary (the server bounds the map
 * rather than trusting keys — see set_assignment_task_done), just a short
 * stable id, so a tiny non-crypto hash is the right tool. Normalised to
 * lower-case with collapsed whitespace so retyped spacing keeps the tick.
 */
export const taskKey = (text) => {
  const normalised = (text || '').toLowerCase().replace(/\s+/g, ' ').trim();
  let hash = 0x811c9dc5;
  for (let i = 0; i < normalised.length; i += 1) {
    hash ^= normalised.charCodeAt(i);
    // Math.imul keeps the multiply in 32-bit; a plain * overflows to a float
    // and starts colliding.
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

/**
 * Tasks in a description, in order. `[]` when the description is empty.
 *
 * Two tasks with identical text collide on key, so they share a tick. Left
 * alone on purpose: the alternative is salting with the index, which puts us
 * back to ticks moving when a line is inserted. Duplicate lines in one
 * assignment are a typo, not a case to design around.
 */
export const parseTasks = (description) => (
  (description || '')
    .split(/\r?\n/)
    .map(stripBullet)
    .filter(Boolean)
    .map((text) => ({ key: taskKey(text), text }))
);

/** True when this task has been ticked. `task_state` maps key -> timestamp. */
export const isTaskDone = (taskState, key) => !!(taskState || {})[key];

/** How many of `tasks` are ticked, for the "3 of 5" counter. */
export const countDone = (tasks, taskState) => (
  (tasks || []).filter((t) => isTaskDone(taskState, t.key)).length
);

/**
 * Whether a description is worth rendering as a checklist at all.
 *
 * A one-line description is a sentence, not a list, and giving it a lone
 * checkbox makes an instruction look like a task. Two or more lines is a list.
 */
export const isChecklist = (description) => parseTasks(description).length > 1;
